import React, { useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { X } from 'lucide-react';

export type GridStyle = 'jiugong' | 'tianzige' | 'mizige';

/** Draw border + grid lines on canvas (matches on-screen SVG layout). */
export function drawWritingGridOnCanvas(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  gridStyle: GridStyle
) {
  const pad = Math.round(Math.min(w, h) * 0.04);
  const inner = Math.min(w, h) - 2 * pad;
  const x0 = pad;
  const y0 = pad;
  const x1 = pad + inner;
  const y1 = pad + inner;
  const midX = pad + inner / 2;
  const midY = pad + inner / 2;

  ctx.strokeStyle = 'rgba(0,0,0,0.15)';
  ctx.lineWidth = 1;
  ctx.strokeRect(pad + 0.5, pad + 0.5, inner - 1, inner - 1);

  ctx.beginPath();
  if (gridStyle === 'jiugong') {
    for (let i = 1; i < 3; i++) {
      const vx = pad + (inner * i) / 3;
      ctx.moveTo(vx, y0);
      ctx.lineTo(vx, y1);
      const hy = pad + (inner * i) / 3;
      ctx.moveTo(x0, hy);
      ctx.lineTo(x1, hy);
    }
  } else if (gridStyle === 'tianzige') {
    ctx.setLineDash([6, 9]);
    ctx.moveTo(midX, y0);
    ctx.lineTo(midX, y1);
    ctx.moveTo(x0, midY);
    ctx.lineTo(x1, midY);
  } else {
    ctx.moveTo(midX, y0);
    ctx.lineTo(midX, y1);
    ctx.moveTo(x0, midY);
    ctx.lineTo(x1, midY);
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.moveTo(x1, y0);
    ctx.lineTo(x0, y1);
  }
  ctx.stroke();
  ctx.setLineDash([]);
}

function clientToCanvas(canvas: HTMLCanvasElement, clientX: number, clientY: number) {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  return {
    x: (clientX - rect.left) * scaleX,
    y: (clientY - rect.top) * scaleY,
  };
}

const SUPPORTS_POINTER =
  typeof globalThis !== 'undefined' && typeof globalThis.PointerEvent !== 'undefined';

interface ChineseWritingGridProps {
  gridStyle?: GridStyle;
  modelCharacter?: string;
  /** When true, no drawing or clear control — display-only (e.g. answer reveal). */
  readOnly?: boolean;
  /** Tailwind / classes for the model character overlay (default faint guide). */
  modelClassName?: string;
  /** Optional inline style for model character (e.g. font size on small preview grids). */
  modelStyle?: React.CSSProperties;
  onClear?: () => void;
  /** Called when ink appears or after clear (e.g. to toggle Check vs “I am not sure”). */
  onInkChange?: (hasInk: boolean) => void;
  className?: string;
  size?: number;
}

export interface ChineseWritingGridRef {
  clearCanvas: () => void;
  /** True if the drawing layer has any non-transparent pixels. */
  hasUserInk: () => boolean;
  /** PNG data URL of the user's strokes (transparent where empty). */
  toDataURL: () => string | null;
  /** Strokes composited over the same grid + border as on screen (for “Your attempt” thumbnails). */
  toCompositeDataURL: () => string | null;
}

const ChineseWritingGrid = forwardRef<ChineseWritingGridRef, ChineseWritingGridProps>(function ChineseWritingGrid({
  gridStyle = 'tianzige',
  modelCharacter,
  readOnly = false,
  modelClassName,
  modelStyle,
  onClear,
  onInkChange,
  className = '',
  size = 400,
}, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const isDrawing = useRef(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const capturePointerIdRef = useRef<number | null>(null);
  const inkNotifyRafRef = useRef<number | null>(null);

  const CANVAS_SIZE = 400;

  const computeHasUserInk = useCallback((): boolean => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return false;
    try {
      const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3]! > 8) return true;
      }
    } catch {
      return false;
    }
    return false;
  }, []);

  const notifyInkAfterPaint = useCallback(() => {
    if (!onInkChange) return;
    if (inkNotifyRafRef.current != null) cancelAnimationFrame(inkNotifyRafRef.current);
    inkNotifyRafRef.current = requestAnimationFrame(() => {
      inkNotifyRafRef.current = null;
      onInkChange(computeHasUserInk());
    });
  }, [onInkChange, computeHasUserInk]);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    onClear?.();
    onInkChange?.(false);
  }, [onClear, onInkChange]);

  const toDataURL = useCallback((): string | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    try {
      return canvas.toDataURL('image/png');
    } catch {
      return null;
    }
  }, []);

  const toCompositeDataURL = useCallback((): string | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    try {
      const w = CANVAS_SIZE;
      const h = CANVAS_SIZE;
      const out = document.createElement('canvas');
      out.width = w;
      out.height = h;
      const ctx = out.getContext('2d');
      if (!ctx) return null;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      drawWritingGridOnCanvas(ctx, w, h, gridStyle);
      ctx.drawImage(canvas, 0, 0);
      return out.toDataURL('image/png');
    } catch {
      return null;
    }
  }, [gridStyle]);

  useImperativeHandle(
    ref,
    () => ({
      clearCanvas,
      hasUserInk: computeHasUserInk,
      toDataURL,
      toCompositeDataURL,
    }),
    [clearCanvas, computeHasUserInk, toDataURL, toCompositeDataURL]
  );

  const strokeSegment = useCallback((x: number, y: number) => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    notifyInkAfterPaint();
  }, [notifyInkAfterPaint]);

  const startPointerDraw = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (readOnly) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      const canvas = canvasRef.current;
      const ctx = ctxRef.current;
      if (!canvas || !ctx) return;
      try {
        canvas.setPointerCapture(e.pointerId);
        capturePointerIdRef.current = e.pointerId;
      } catch {
        capturePointerIdRef.current = null;
      }
      isDrawing.current = true;
      const { x, y } = clientToCanvas(canvas, e.clientX, e.clientY);
      lastPoint.current = { x, y };
      ctx.beginPath();
      ctx.moveTo(x, y);
    },
    [readOnly]
  );

  const drawPointer = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawing.current) return;
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      const { x, y } = clientToCanvas(canvas, e.clientX, e.clientY);
      strokeSegment(x, y);
      lastPoint.current = { x, y };
    },
    [strokeSegment]
  );

  const endPointerDraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas && capturePointerIdRef.current != null) {
      try {
        canvas.releasePointerCapture(capturePointerIdRef.current);
      } catch {
        /* already released */
      }
      capturePointerIdRef.current = null;
    }
    isDrawing.current = false;
    lastPoint.current = null;
    notifyInkAfterPaint();
  }, [notifyInkAfterPaint]);

  const getCoords = useCallback((e: React.TouchEvent | React.MouseEvent): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    return clientToCanvas(canvas, clientX, clientY);
  }, []);

  const startDraw = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement> | React.MouseEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      const ctx = ctxRef.current;
      if (!ctx) return;
      isDrawing.current = true;
      const { x, y } = getCoords(e);
      lastPoint.current = { x, y };
      ctx.beginPath();
      ctx.moveTo(x, y);
    },
    [getCoords]
  );

  const draw = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement> | React.MouseEvent<HTMLCanvasElement>) => {
      if (!isDrawing.current) return;
      e.preventDefault();
      const { x, y } = getCoords(e);
      strokeSegment(x, y);
      lastPoint.current = { x, y };
    },
    [getCoords, strokeSegment]
  );

  const stopDraw = useCallback(() => {
    isDrawing.current = false;
    lastPoint.current = null;
    notifyInkAfterPaint();
  }, [notifyInkAfterPaint]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = CANVAS_SIZE;
    canvas.height = CANVAS_SIZE;
    ctxRef.current = canvas.getContext('2d', { willReadFrequently: true });
  }, []);

  useEffect(
    () => () => {
      if (inkNotifyRafRef.current != null) cancelAnimationFrame(inkNotifyRafRef.current);
    },
    []
  );

  const strokeWidth = 1;
  const strokeColor = 'rgba(0,0,0,0.15)';

  const renderGrid = () => {
    const s = 100;
    const lines: React.ReactNode[] = [];

    if (gridStyle === 'jiugong') {
      for (let i = 1; i < 3; i++) {
        lines.push(
          <line
            key={`v${i}`}
            x1={(i * s) / 3}
            y1={0}
            x2={(i * s) / 3}
            y2={s}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
        lines.push(
          <line
            key={`h${i}`}
            x1={0}
            y1={(i * s) / 3}
            x2={s}
            y2={(i * s) / 3}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
      }
    } else if (gridStyle === 'tianzige') {
      const dotted = { strokeDasharray: '2 3' };
      lines.push(
        <line key="v" x1={s / 2} y1={0} x2={s / 2} y2={s} stroke={strokeColor} strokeWidth={strokeWidth} {...dotted} />,
        <line key="h" x1={0} y1={s / 2} x2={s} y2={s / 2} stroke={strokeColor} strokeWidth={strokeWidth} {...dotted} />
      );
    } else {
      lines.push(
        <line key="v" x1={s / 2} y1={0} x2={s / 2} y2={s} stroke={strokeColor} strokeWidth={strokeWidth} />,
        <line key="h" x1={0} y1={s / 2} x2={s} y2={s / 2} stroke={strokeColor} strokeWidth={strokeWidth} />,
        <line key="d1" x1={0} y1={0} x2={s} y2={s} stroke={strokeColor} strokeWidth={strokeWidth} />,
        <line key="d2" x1={s} y1={0} x2={0} y2={s} stroke={strokeColor} strokeWidth={strokeWidth} />
      );
    }

    return lines;
  };

  const modelSpanClass =
    modelClassName ?? 'text-gray-200';

  const defaultModelFontSize =
    readOnly && modelCharacter
      ? `${Math.round(size * 0.76)}px`
      : 'min(70vmin, 320px)';

  return (
    <div
      className={`relative select-none [-webkit-touch-callout:none] ${readOnly ? 'pointer-events-none' : ''} ${className}`}
    >
      <div
        className="relative aspect-square w-full max-w-[min(100vw-2rem,480px)] mx-auto rounded-xl border-2 border-gray-200 bg-white overflow-hidden touch-none select-none"
        style={{ maxWidth: size }}
      >
        <div
          className={`absolute inset-0 flex items-center justify-center ${readOnly ? 'p-2' : 'p-4'}`}
        >
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid meet"
            className="w-full h-full max-w-full max-h-full"
          >
            <rect
              x={1}
              y={1}
              width={98}
              height={98}
              fill="none"
              stroke={strokeColor}
              strokeWidth={strokeWidth}
            />
            {renderGrid()}
          </svg>
        </div>
        {modelCharacter && (
          <div
            className={`absolute inset-0 flex items-center justify-center pointer-events-none box-border ${
              readOnly ? 'p-[3%]' : 'p-[8%]'
            }`}
            aria-hidden
          >
            <span
              className={`font-serif select-none leading-none ${modelSpanClass}`}
              style={{
                fontFamily: 'KaTi Maobi, KaiTi, serif',
                ...modelStyle,
                fontSize: modelStyle?.fontSize ?? defaultModelFontSize,
              }}
            >
              {modelCharacter}
            </span>
          </div>
        )}
        <canvas
          ref={canvasRef}
          className={`absolute inset-0 w-full h-full select-none ${readOnly ? 'pointer-events-none opacity-0' : 'cursor-crosshair'}`}
          style={{
            touchAction: readOnly ? 'auto' : 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
          }}
          {...(readOnly
            ? {}
            : SUPPORTS_POINTER
              ? {
                  onPointerDown: startPointerDraw,
                  onPointerMove: drawPointer,
                  onPointerUp: endPointerDraw,
                  onPointerCancel: endPointerDraw,
                }
              : {
                  onMouseDown: startDraw,
                  onMouseMove: draw,
                  onMouseUp: stopDraw,
                  onMouseLeave: stopDraw,
                  onTouchStart: startDraw,
                  onTouchMove: draw,
                  onTouchEnd: stopDraw,
                  onTouchCancel: stopDraw,
                })}
        />
        {!readOnly && (
          <button
            type="button"
            onClick={clearCanvas}
            className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 active:scale-95 touch-manipulation"
            aria-label="Clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
});

export default ChineseWritingGrid;
